import {
  createComponentAdmin,
  PublishesTo,
  SubscribesTo,
  type SchemaTypeHelper,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'Confetti' as const;
export const name = reactName;
export const description = reactName;

export const DEFAULT_CONFETTI_COLORS = [
  '#fcf403',
  '#62fc03',
  '#f4fc03',
  '#03e7fc',
  '#03fca5',
  '#a503fc',
  '#fc03ad',
  '#fc03c2',
];

export const DEFAULT_EMOJIS = ['🌈', '⚡️', '💥', '✨', '💫', '🌸'];

const schema = Type.Object({
  confettiRadius: Type.Optional(
    Type.Number({
      title: 'Confetti Radius',
      description: 'The size of the confetti in pixels',
      default: 6,
    })
  ),
  confettiNumber: Type.Optional(
    Type.Number({
      title: 'Confetti Count',
      description: 'The amount of confetti to display',
      default: 250,
    })
  ),
  partyBlastCount: Type.Optional(
    Type.Number({
      title: 'Party Blast Count',
      description:
        'The number of confetti blasts to fire during a "confetti party", this can be overridden in the flow controller',
      default: 10,
    })
  ),
  confettiColors: Type.Optional(
    Type.Array(Type.String({title: 'Color'}), {
      title: 'Colors',
      description: 'Set the confetti colors to display',
      maxItems: 8,
      minItems: 0,
    })
  ),

  emojiConfetti: Type.Optional(
    Type.Object(
      {
        useEmojiConfetti: Type.Optional(
          Type.Boolean({
            title: 'Use Emoji Confetti',
            description: 'Every other confetti blast will emit a set of emojis',
            default: false,
          })
        ),
      },
      {
        title: 'Emoji Confetti',
        dependencies: {
          useEmojiConfetti: {
            oneOf: [
              {
                properties: {
                  useEmojiConfetti: {
                    enum: [false],
                  },
                },
              },
              {
                properties: {
                  useEmojiConfetti: {
                    enum: [true],
                  },
                  alternate: Type.Optional(
                    Type.Boolean({
                      title: 'Alternate Emoji and Confetti',
                      description:
                        'When confetti is fired, the confetti will alternate between colors and Emoji. If this is set to off, only emoji will fire',
                      default: true,
                    })
                  ),
                  emojis: Type.Optional(
                    Type.Array(Type.String({title: 'Emoji'}), {
                      title: 'Emojis',
                      description:
                        'The selection of emojis to use when firing emoji confetti blasts',
                      maxItems: 6,
                      minItems: 0,
                      default: DEFAULT_EMOJIS,
                    })
                  ),
                  emojiSize: Type.Optional(
                    Type.Number({
                      title: 'Emoji Size',
                      description: 'The size of the emoji confetti in pixels',
                      default: 80,
                    })
                  ),
                  emojiConfettiNumber: Type.Optional(
                    Type.Number({
                      title: 'Emoji Confetti Count',
                      description:
                        'The amount of confetti to display (capped at 60)',
                      default: 40,
                    })
                  ),
                },
              },
            ],
          },
        },
      }
    )
  ),
});

const defaultFieldData: Static<typeof schema> = {
  emojiConfetti: {useEmojiConfetti: false},
  confettiNumber: 250,
  confettiRadius: 6,
  partyBlastCount: 10,
};

const instructions = Type.Union([
  SubscribesTo({
    topic: `${reactName}:fire-confetti`,
    description: 'Fire a single confetti blast',
  }),
  SubscribesTo({
    topic: `${reactName}:confetti-party`,
    description: 'Start the party, fire multiple confetti blasts',
    meta: {
      blastCount: Type.Optional(
        Type.String({
          title: 'Blast Count',
          description: 'The number of blasts to fire during a confetti party',
        })
      ),
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-confetti-fire`,
    description: 'Confetti has been fired',
  }),
  PublishesTo({
    topic: `${reactName}:on-confetti-complete`,
    description: 'Confetti has been fired and the animation is complete',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '5f0fd730-4e6d-45eb-a5d2-8741052c9851',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema: {
    confettiColors: {
      items: {
        'ui:widget': 'color',
      },
    },
  },
  instructions,
  category: 'custom',
})
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
